import React, { useState, useRef, useEffect } from 'react';
import { Tag, X, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Inline promo attach/swap/remove cell for the admin Agencies table.
 * Shows the current pending promo code as a pill; clicking opens a dropdown
 * of active promotions (filtered by the agency's mode) + a "Clear" option.
 */
export default function AgencyPromoCell({ agency, api, activePromos, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const code = agency.pending_promotion_code;
  const currentId = agency.pending_promotion_id;
  const mode = agency.is_lifetime ? 'lifetime' : 'monthly';
  const options = (activePromos || []).filter(p => (p.applies_to || []).includes(mode));

  const setPromo = async (promo_id) => {
    setSaving(true);
    try {
      await api.put(`/api/admin/agencies/${agency.workspace_id}/promotion`, { promo_id: promo_id || null });
      toast.success(promo_id ? 'Promotion updated' : 'Promotion cleared');
      setOpen(false);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={saving}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-colors max-w-[160px] truncate ${
          code
            ? 'bg-teal-400/10 text-teal-300 border border-teal-400/30 hover:bg-teal-400/15'
            : 'bg-white/[0.04] text-slate-500 border border-white/10 hover:text-slate-300'
        }`}
        title={code ? `Current: ${code}` : 'Attach a promo code'}
        data-testid={`agency-promo-cell-${agency.workspace_id}`}
      >
        {code ? (
          <>
            <Tag size={11} />
            <code className="font-mono truncate">{code}</code>
          </>
        ) : (
          <>
            <Tag size={11} />
            <span>Add…</span>
          </>
        )}
        <ChevronDown size={11} />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-64 rounded-xl border border-white/10 bg-[#0c0d12] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8)] p-1.5"
          data-testid={`agency-promo-dropdown-${agency.workspace_id}`}
        >
          <div className="max-h-64 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-2.5 text-xs text-slate-500">No active {mode} promos. Create one in the Create tab.</div>
            ) : (
              options.map((p) => (
                <button
                  key={p.promotion_id}
                  onClick={() => setPromo(p.promotion_id)}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left hover:bg-white/[0.04]"
                  data-testid={`agency-promo-option-${p.promotion_id}`}
                >
                  <span className="flex-1 min-w-0">
                    <code className="block text-xs font-mono font-bold text-white truncate">{p.code}</code>
                    <span className="block text-[10px] text-slate-400 truncate">
                      {p.discount_type === 'percent' ? `${p.discount_value}% off` : `$${p.discount_value} off`}
                      {p.duration === 'repeating' ? ` · ${p.duration_in_months}mo` : p.duration === 'forever' ? ' · forever' : ' · once'}
                      {p.trial_days > 0 ? ` · +${p.trial_days}d trial` : ''}
                    </span>
                  </span>
                  {currentId === p.promotion_id && <Check size={14} className="text-teal-400 flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
          {code && (
            <button
              onClick={() => setPromo(null)}
              className="w-full mt-1 flex items-center gap-2 px-2.5 py-2 rounded-lg text-left hover:bg-rose-400/10 text-rose-400 border-t border-white/5"
              data-testid={`agency-promo-clear-${agency.workspace_id}`}
            >
              <X size={12} />
              <span className="text-xs">Clear promotion</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
